import { useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  useRoleList,
  useEmployeeFilters,
  useEmployeeSelection,
  usePersonnelEmployees,
  usePersonnelStores,
  usePersonnelDeleteEmployee,
  usePersonnelBulkDeleteEmployees,
  useCurrentUser,
} from '@personnel/presentation/hooks';
import {
  PageLayout,
  PrimaryHeader,
  SecondaryHeader,
  PersonnelTabs,
  Pagination,
  useDialog,
  withDialog,
} from '@personnel/presentation/components/shared';
import { EmployeesSearchBar, EmployeesTable } from '@personnel/presentation/components/list';
import { toast } from 'sonner';
import { validateEmployeeDelete, filterDeletableEmployees } from '@personnel/domain/services';
import type { VerificationStatusType } from '@personnel/domain/types';

const PAGE_SIZE = 10;

const EmployeesListPageComponent = (): React.ReactElement => {
  // Translations for 'employees' and 'personnel' namespaces
  const { t } = useTranslation('personnel-employees');
  const { t: tPersonnel } = useTranslation('personnel');
  const { showDialog } = useDialog();
  // Current user context for validation
  const { uuid: currentUserUuid } = useCurrentUser();
  // Filters and pagination synced with the URL (single source of truth)
  const { filters, page: currentPage, setFilters, setPage, isReady: filtersReady } = useEmployeeFilters();

  // Data hook: fetches employees with search and pagination parameters
  // Uses composition layer - no repository creation needed
  const { isLoading, data } = usePersonnelEmployees(
    {
      search: filters.user,
      roleUuids: filters.role.length ? filters.role : undefined,
      storeUuids: filters.store.length ? filters.store : undefined,
      verificationStatus: filters.status.length ? (filters.status as VerificationStatusType[]) : undefined,
      includeAssignedStores: true,
      page: currentPage,
      limit: PAGE_SIZE,
    },
    {
      enabled: filtersReady,
    },
  );

  const { selectedEmployees, selectedCount, toggleOne, toggleAll, clearSelection } = useEmployeeSelection(
    data?.data ? [...data.data] : [],
  );

  // Delete hooks: use composition layer for dependency injection
  const { deleteEmployee, isLoading: isDeleting } = usePersonnelDeleteEmployee({
    onSuccess: clearSelection,
  });

  const { bulkDeleteEmployees, isLoading: isBulkDeleting } = usePersonnelBulkDeleteEmployees({
    onSuccess: clearSelection,
  });

  const { roles } = useRoleList();

  // Stores hook: uses composition layer, auto-detects organization
  const { data: storesData } = usePersonnelStores();

  const totalPages = data?.pagination ? Math.ceil((data.pagination.count ?? 0) / PAGE_SIZE) : 0;

  // Clamp the URL page param to the actual page count. Uses replace to avoid
  // polluting history when the user landed via deep link with ?page=9999.
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setPage(totalPages, { replace: true });
    }
  }, [currentPage, totalPages, setPage]);

  /**
   * Shows dialog explaining why an employee cannot be deleted
   */
  const openBlockedDeleteDialog = (reason: 'self_user' | 'install_user') => {
    const isSelfUser = reason === 'self_user';
    showDialog({
      title: isSelfUser ? t('dialog.selfUser.title') : t('dialog.cantDelete.title'),
      content: (
        <Trans
          components={{ bold: <strong />, br: <br /> }}
          i18nKey={isSelfUser ? 'dialog.selfUser.message' : 'dialog.cantDelete.message'}
          t={t}
        />
      ),
      cancelText: t('dialog.selfUser.buttonCancel'),
    });
  };

  /**
   * Shows confirmation dialog and executes delete
   */
  const openDeleteDialog = (employee?: { uuid: string; roleName?: string; permissions?: ReadonlyArray<string> }) => {
    showDialog({
      title: t('dialog.deleteMultiple.title'),
      content: <Trans components={{ bold: <strong />, br: <br /> }} i18nKey="dialog.deleteMultiple.message" t={t} />,
      confirmText: t('dialog.deleteMultiple.confirmButton'),
      onConfirm: async () => {
        if (employee?.uuid) {
          deleteEmployee(employee.uuid);
          return;
        }
        // Filter employees using domain service
        const deletableEmployees = filterDeletableEmployees(selectedEmployees, currentUserUuid);
        const uuidsToDelete = deletableEmployees.map((emp) => emp.uuid);
        if (uuidsToDelete.length === 0) {
          toast.error(t('notifications.deleteError'), { duration: 3000 });
          return;
        }
        await bulkDeleteEmployees(uuidsToDelete);
      },
    });
  };

  /**
   * Handles delete action using domain validation service
   */
  const handleDelete = (employee?: { uuid: string; roleName?: string; permissions?: ReadonlyArray<string> }) => {
    if (!employee?.uuid) {
      // Bulk delete - open confirmation dialog
      openDeleteDialog();
      return;
    }

    // Use domain service for validation
    const validation = validateEmployeeDelete(employee, currentUserUuid);

    if (!validation.canDelete) {
      openBlockedDeleteDialog(validation.reason);
      return;
    }

    openDeleteDialog(employee);
  };

  return (
    <PageLayout>
      {/* Main header for the Personnel section */}
      <PrimaryHeader description={tPersonnel('section.description')} title={tPersonnel('section.title')} />
      {/* Navigation tabs for the Personnel section */}
      <PersonnelTabs activeTab={'users'} onTabChange={() => {}} />
      {/* Secondary header specific to the employees list */}
      <SecondaryHeader description={t('list.description')} title={t('list.title')} />
      {/* Employee search bar */}
      <EmployeesSearchBar filters={filters} onFiltersChange={setFilters} roles={roles} stores={storesData || []} />
      {/* Employees table: uses mock data while 'employees' is being integrated */}
      <EmployeesTable
        employees={data?.data ? [...data.data] : []}
        isLoading={isLoading || isDeleting || isBulkDeleting}
        onDeleteEmployee={handleDelete}
        onToggleAll={toggleAll}
        onToggleOne={toggleOne}
        selectedEmployees={selectedEmployees}
      />
      {/* Pagination */}
      {data?.pagination && data?.pagination.count > 0 && (
        <Pagination
          currentPage={currentPage}
          onDeleteClick={() => handleDelete()}
          onPageChange={setPage}
          selectedCount={selectedCount}
          totalItems={data?.pagination?.count ?? 0}
          totalPages={totalPages}
        />
      )}
    </PageLayout>
  );
};

export const EmployeesListPage = withDialog(EmployeesListPageComponent);
